import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiDownload, FiFileText, FiFilter, FiX } from 'react-icons/fi';
import { LocationsService, ReportsService, SalesService } from '../services/ZashaniService';
import { ButtonSpinner, MetricSkeleton, TableEmptyState } from '../components/LoadingStates';
import { useAuth } from '../context/AuthContext';
import { buildTopProducts, exportSalesReportExcel, exportSalesReportPdf } from '../utils/reportExports';

const currency = new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' });

const initialFilters = {
    mode: 'all',
    day: '',
    month: '',
    from: '',
    to: '',
    department: '',
    municipality: '',
};

const formatDateInput = (date) => date.toISOString().slice(0, 10);

const getMonthRange = (monthValue) => {
    if (!monthValue) return {};
    const [year, month] = monthValue.split('-').map(Number);
    return {
        dateFrom: formatDateInput(new Date(year, month - 1, 1)),
        dateTo: formatDateInput(new Date(year, month, 0)),
    };
};

const getFilterRange = (filters) => {
    if (filters.mode === 'day' && filters.day) return { dateFrom: filters.day, dateTo: filters.day };
    if (filters.mode === 'month') return getMonthRange(filters.month);
    if (filters.mode === 'range') return { dateFrom: filters.from || undefined, dateTo: filters.to || undefined };
    return {};
};

const getReportParams = (filters) => ({
    ...getFilterRange(filters),
    department: filters.department || undefined,
    municipality: filters.municipality || undefined,
});

const buildSummaryFromSales = (sales) => sales.reduce((summary, sale) => {
    summary.sales += Number(sale.totals?.saleTotal || 0);
    summary.netProfit += Number(sale.totals?.netProfit || 0);
    summary.codCommission += Number(sale.totals?.codCommission || 0);
    summary.orders += 1;
    return summary;
}, { sales: 0, netProfit: 0, codCommission: 0, orders: 0 });

const Reports = () => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [sales, setSales] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState('');
    const [message, setMessage] = useState('');

    const reportParams = useMemo(() => getReportParams(filters), [filters]);
    const filteredSales = sales;
    const summary = useMemo(() => data?.summary || buildSummaryFromSales(filteredSales), [data, filteredSales]);
    const topProducts = useMemo(() => buildTopProducts(filteredSales), [filteredSales]);
    const municipalities = useMemo(
        () => departments.find((department) => department.name === filters.department)?.municipalities || [],
        [departments, filters.department],
    );

    const load = useCallback(async (paramsOverride = reportParams) => {
        setLoading(true);
        setMessage('');
        try {
            const [summaryRows, saleRows, locationRows] = await Promise.all([
                ReportsService.financialSummary(paramsOverride),
                SalesService.listAll(paramsOverride),
                LocationsService.departments(),
            ]);
            setData(summaryRows);
            setSales(saleRows);
            setDepartments(locationRows);
        } catch (error) {
            setData(null);
            setMessage(error.response?.data?.message || 'No fue posible cargar reportes.');
        } finally {
            setLoading(false);
        }
    }, [reportParams]);

    useEffect(() => {
        load();
    }, [load]);

    const applyFilters = async (event) => {
        event.preventDefault();
        await load();
    };

    const clearFilters = async () => {
        setFilters(initialFilters);
        await load({});
    };

    const downloadPdf = async () => {
        setExporting('pdf');
        try {
            await exportSalesReportPdf({ sales: filteredSales, filters, user });
        } finally {
            setExporting('');
        }
    };

    const downloadExcel = () => {
        setExporting('excel');
        try {
            exportSalesReportExcel({ sales: filteredSales, filters, user });
        } finally {
            setExporting('');
        }
    };

    return (
        <section className="content-section">
            <div className="section-heading">
                <div><p className="eyebrow">Finanzas</p><h2>Reportes</h2></div>
                <div className="report-actions">
                    <button className="primary-button" type="button" onClick={downloadPdf} disabled={loading || exporting || filteredSales.length === 0}>
                        {exporting === 'pdf' ? <ButtonSpinner /> : <FiFileText />}
                        <span>{exporting === 'pdf' ? 'Generando...' : 'PDF'}</span>
                    </button>
                    <button className="ghost-button" type="button" onClick={downloadExcel} disabled={loading || exporting || filteredSales.length === 0}>
                        {exporting === 'excel' ? <ButtonSpinner /> : <FiDownload />}
                        <span>{exporting === 'excel' ? 'Generando...' : 'Excel'}</span>
                    </button>
                </div>
            </div>

            <form className="filter-panel report-filter" onSubmit={applyFilters}>
                <label>Filtro
                    <select value={filters.mode} onChange={(event) => setFilters({ ...filters, mode: event.target.value })}>
                        <option value="all">Todo</option>
                        <option value="day">Por dia</option>
                        <option value="month">Por mes</option>
                        <option value="range">Rango</option>
                    </select>
                </label>
                {filters.mode === 'day' && (
                    <label>Dia
                        <input type="date" value={filters.day} onChange={(event) => setFilters({ ...filters, day: event.target.value })} />
                    </label>
                )}
                {filters.mode === 'month' && (
                    <label>Mes
                        <input type="month" value={filters.month} onChange={(event) => setFilters({ ...filters, month: event.target.value })} />
                    </label>
                )}
                {filters.mode === 'range' && (
                    <>
                        <label>Desde
                            <input type="date" value={filters.from} onChange={(event) => setFilters({ ...filters, from: event.target.value })} />
                        </label>
                        <label>Hasta
                            <input type="date" value={filters.to} onChange={(event) => setFilters({ ...filters, to: event.target.value })} />
                        </label>
                    </>
                )}
                <label>Departamento
                    <select value={filters.department} onChange={(event) => setFilters({ ...filters, department: event.target.value, municipality: '' })}>
                        <option value="">Todo el pais</option>
                        {departments.map((department) => <option key={department.code} value={department.name}>{department.name}</option>)}
                    </select>
                </label>
                <label>Municipio
                    <select value={filters.municipality} onChange={(event) => setFilters({ ...filters, municipality: event.target.value })} disabled={!filters.department}>
                        <option value="">Todos los municipios</option>
                        {municipalities.map((municipality) => <option key={municipality.code} value={municipality.name}>{municipality.name}</option>)}
                    </select>
                </label>
                <button className="ghost-button" type="submit" disabled={loading}>
                    <FiFilter />
                    <span>Filtrar</span>
                </button>
                <button className="ghost-button muted" type="button" disabled={loading} onClick={clearFilters}>
                    <FiX />
                    <span>Limpiar</span>
                </button>
            </form>

            {message && <div className="notice">{message}</div>}
            {loading ? (
                <MetricSkeleton count={4} />
            ) : (
                <div className="metric-grid">
                    <article className="metric-card"><span>Total vendido</span><strong>{currency.format(summary?.sales || 0)}</strong></article>
                    <article className="metric-card"><span>Ganancia neta</span><strong>{currency.format(summary?.netProfit || 0)}</strong></article>
                    <article className="metric-card"><span>Comision COD</span><strong>{currency.format(summary?.codCommission || 0)}</strong></article>
                    <article className="metric-card"><span>Pedidos</span><strong>{summary?.orders || 0}</strong></article>
                </div>
            )}
            <div className="panel">
                <div className="table-toolbar">
                    <div>
                        <h2>Inteligencia por ubicacion</h2>
                        <span className="table-subtitle">Departamentos y municipios con mas ventas</span>
                    </div>
                </div>
                <div className="location-report-grid">
                    <div className="table-wrap">
                        {loading || !data?.byDepartment?.length ? (
                            <TableEmptyState loading={loading} emptyText="Todavia no hay ventas por departamento." />
                        ) : (
                            <table>
                                <thead><tr><th>Departamento</th><th>Pedidos</th><th>Ventas</th><th>Ganancia</th></tr></thead>
                                <tbody>
                                    {data.byDepartment.map((item) => (
                                        <tr key={item._id || 'sin-departamento'}>
                                            <td>{item._id || 'Sin departamento'}</td>
                                            <td>{item.orders}</td>
                                            <td>{currency.format(item.sales || 0)}</td>
                                            <td>{currency.format(item.netProfit || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <div className="table-wrap">
                        {loading || !data?.byMunicipality?.length ? (
                            <TableEmptyState loading={loading} emptyText="Todavia no hay ventas por municipio." />
                        ) : (
                            <table>
                                <thead><tr><th>Municipio</th><th>Departamento</th><th>Pedidos</th><th>Ventas</th></tr></thead>
                                <tbody>
                                    {data.byMunicipality.map((item) => (
                                        <tr key={`${item._id?.department || 'sin'}-${item._id?.municipality || 'sin'}`}>
                                            <td>{item._id?.municipality || 'Sin municipio'}</td>
                                            <td>{item._id?.department || 'Sin departamento'}</td>
                                            <td>{item.orders}</td>
                                            <td>{currency.format(item.sales || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
            <div className="panel">
                <div className="table-toolbar">
                    <div>
                        <h2>Productos mas vendidos</h2>
                        <span className="table-subtitle">Ordenados por unidades vendidas</span>
                    </div>
                </div>
                <div className="table-wrap">
                    {loading || topProducts.length === 0 ? (
                        <TableEmptyState loading={loading} emptyText="Todavia no hay productos vendidos." />
                    ) : (
                        <table>
                            <thead><tr><th>#</th><th>Codigo</th><th>Unidades</th><th>Ventas</th><th>Costo</th><th>Margen</th></tr></thead>
                            <tbody>
                                {topProducts.map((item, index) => (
                                    <tr key={item.code}>
                                        <td>{index + 1}</td>
                                        <td>{item.code}</td>
                                        <td>{item.units}</td>
                                        <td>{currency.format(item.salesTotal)}</td>
                                        <td>{currency.format(item.costTotal)}</td>
                                        <td>{currency.format(item.profitTotal)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Reports;
